<template>
  <div class="sf-container">
    <div class="sf-std-content tw-relative tw-left-0 tw-top-0 tw-text-center">
      <div>
        <h1 v-if="$currentUser.value" class="tw-text-primary">
          Welcome {{ $currentUser.value.fullName }}
        </h1>
        <h1 v-else class="tw-text-primary">Welcome</h1>
      </div>
      <div class="tw-mb-8 tw-max-w-5xl tw-mx-auto">
        Choose a spelling rule then choose to either practise the spelling or
        take a test with those words. All words are read aloud (make sure your
        sound is turned up) and provided within a sentence for context.
      </div>
      <div class="tw-max-w-[900px] tw-mx-auto">
        <HomeSpellingRuleCategory
          v-if="currentCategory"
          :category="currentCategory"
          :parent-group="parentGroup"
        />
        <ul v-else class="tw-grid tw-gap-4">
          <HomeSpellingRuleCategoryTree
            v-for="(c, i) in categories"
            :category="c"
            :key="c.id"
            :expanded-id="expandedId"
            @expand="toggleExpand"
            :class="getCategoryClass(i)"
            class="tw-text-4xl tw-font-bold tw-rounded-2xl tw-text-center hover:tw-outline tw-outline-mainbg1 tw-outline-offset-[-8px]"
          />
        </ul>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.cat-full {
  @apply tw-col-span-2;
}
.cat-half {
  @apply tw-col-span-2 md:tw-col-span-1;
}

.cat-first {
  @apply tw-bg-color5;
}

.cat-middle {
  @apply tw-bg-color2;
}

.cat-last {
  @apply tw-bg-primary;
}
</style>

<script lang="ts">
import HomeSpellingRuleCategoryTree from "@/components/home/HomeSpellingRuleCategoryTree.vue";
import HomeSpellingRuleCategory from "@/components/home/HomeSpellingRuleCategory.vue";
import apiCall from "@/func/net/apiCall";
import buildRulesTree from "@/func/spelling/buildRulesTree";
import { SfWordCategory } from "@/types/spellingTypes";
import { defineComponent } from "vue";

export default defineComponent({
  components: { HomeSpellingRuleCategoryTree, HomeSpellingRuleCategory },
  data() {
    return {
      categories: [] as SfWordCategory[],
      expandedId: 0,
    };
  },
  mounted() {
    this.refreshRules(false);
  },
  beforeUnmount() {
    this.categories = [];
  },
  computed: {
    categoryId(): number {
      if (!this.$route.params.ruleId) {
        return 0;
      }
      return parseInt(this.$route.params.ruleId as string);
    },
    currentCategory(): SfWordCategory | null {
      if (this.categoryId == 0) {
        return null;
      }
      return this.getCategory(this.categoryId, this.categories);
    },
    parentGroup(): "first" | "middle" | "last" | "" {
      const catId = this.categoryId;
      if (catId == 0) {
        return "";
      }
      for (let i in this.categories) {
        if (this.isParentCategory(catId, this.categories[i])) {
          return this.getGroupType(parseInt(i));
        }
      }
      return "first";
    },
  },
  methods: {
    async refreshRules(all: boolean) {
      const params = all ? { all: 1 } : null;
      const resData = await apiCall("spelling-rule/home", params);
      if (resData.success) {
        this.categories = buildRulesTree(resData.data.items);
      }
    },
    getGroupType(index: number) {
      const limit = this.categories.length % 2 == 0 ? 2 : 3;
      if (index == 0) {
        return "first";
      } else if (index > this.categories.length - limit) {
        return "last";
      }
      return "middle";
    },
    getCategoryClass(index: number) {
      const type = this.getGroupType(index);
      if (type == "middle") {
        return "cat-half cat-middle";
      }
      return "cat-full cat-" + type;
    },
    toggleExpand(catId: number) {
      this.expandedId = this.expandedId == catId ? 0 : catId;
    },
    isParentCategory(catId: number, cat: SfWordCategory): boolean {
      if (catId == cat.id) {
        return true;
      }
      if (cat.subCategories) {
        for (const sc of cat.subCategories) {
          if (this.isParentCategory(catId, sc)) {
            return true;
          }
        }
      }
      return false;
    },
    getCategory(
      catId: number,
      catList: SfWordCategory[],
    ): SfWordCategory | null {
      for (const cat of catList) {
        if (cat.id == catId) {
          return cat;
        }
        if (cat.subCategories) {
          const res = this.getCategory(catId, cat.subCategories);
          if (res) {
            return res;
          }
        }
      }
      return null;
    },
  },
});
</script>
